import {
    collection,
    query,
    where,
    getDocs,
    addDoc,
    updateDoc,
    doc,
    orderBy,
    limit,
    Timestamp,
    onSnapshot,
    Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import { SafetyAlert, FleetStats } from '../../types/safety';

class SafetyService {
    private readonly ALERTS_COLLECTION = 'alerts';

    /**
     * Get current company ID with fallback to user's companyId from storage
     */
    private getCompanyIdFromContext(): string | null {
        const companyId = getCompanyId();
        if (companyId) return companyId;

        // Fallback to current user's companyId from auth storage
        try {
            const storedUser = localStorage.getItem('oilwellx_auth_user') || sessionStorage.getItem('oilwellx_auth_user');
            if (storedUser) {
                const user = JSON.parse(storedUser);
                return user.companyId || null;
            }
        } catch (e) {
        }

        return null;
    }

    /**
     * Get recent safety alerts
     */
    async getRecentAlerts(limitCount: number = 10): Promise<SafetyAlert[]> {
        const companyId = this.getCompanyIdFromContext();
        if (!companyId) {
            return [];
        }

        try {
            const alertsRef = collection(db, `companies/${companyId}/${this.ALERTS_COLLECTION}`);
            const q = query(
                alertsRef,
                orderBy('timestamp', 'desc'),
                limit(limitCount)
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(this.convertDocToAlert);
        } catch (error: any) {
            // Handle permission denied gracefully
            if (error?.code === 'permission-denied') {
                return [];
            }
            console.error('Error fetching alerts:', error);
            return [];
        }
    }

    /**
     * Subscribe to safety alerts (Real-time)
     */
    subscribeToAlerts(callback: (alerts: SafetyAlert[]) => void, limitCount: number = 20): Unsubscribe {
        const companyId = this.getCompanyIdFromContext();
        if (!companyId) {
            callback([]);
            return () => { };
        }

        try {
            const alertsRef = collection(db, `companies/${companyId}/${this.ALERTS_COLLECTION}`);
            const q = query(
                alertsRef,
                where('status', 'in', ['new', 'acknowledged']),
                orderBy('timestamp', 'desc'),
                limit(limitCount)
            );

            return onSnapshot(q, (snapshot) => {
                const alerts = snapshot.docs.map(this.convertDocToAlert);
                callback(alerts);
            }, (error) => {
                // Handle permission denied gracefully
                if (error?.code === 'permission-denied') {
                } else {
                    console.error('SafetyService: Error in alert subscription:', error);
                }
                callback([]);
            });
        } catch (error) {
            console.error('Error setting up alert subscription:', error);
            callback([]);
            return () => { };
        }
    }

    /**
     * Get fleet statistics
     */
    async getFleetStats(): Promise<FleetStats> {
        const companyId = this.getCompanyIdFromContext();

        // Return default stats if no tenant context
        const defaultStats: FleetStats = {
            totalVehicles: 0,
            activeVehicles: 0,
            totalAlerts24h: 0,
            safetyScore: 100,
            fuelEfficiency: 0
        };

        if (!companyId) {
            return defaultStats;
        }

        try {
            // 1. Get Total Users (Potential Fleet)
            const usersRef = collection(db, `companies/${companyId}/users`);
            const usersSnapshot = await getDocs(usersRef);
            const totalVehicles = usersSnapshot.size;

            // 2. Get Active Users (With recent location)
            const activeFleet = await this.getActiveFleet();
            const activeVehicles = activeFleet.length;

            // 3. Get Alerts in last 24h
            const alerts = await this.getRecentAlerts(100);
            const oneDayAgo = new Date();
            oneDayAgo.setDate(oneDayAgo.getDate() - 1);
            const totalAlerts24h = alerts.filter(a => new Date(a.timestamp) > oneDayAgo).length;

            // 4. Calculate Safety Score (Simple algorithm: 100 - (alerts * 5))
            const safetyScore = Math.max(0, 100 - (totalAlerts24h * 5));

            return {
                totalVehicles,
                activeVehicles,
                totalAlerts24h,
                safetyScore,
                fuelEfficiency: 0 // Not currently tracked
            };
        } catch (error: any) {
            // Handle permission denied gracefully
            if (error?.code === 'permission-denied') {
            } else {
                console.error('Error calculating fleet stats:', error);
            }
            return defaultStats;
        }
    }

    /**
     * Get active fleet locations (Real Users)
     */
    async getActiveFleet(): Promise<any[]> {
        const companyId = this.getCompanyIdFromContext();
        if (!companyId) {
            return [];
        }

        try {
            const usersRef = collection(db, `companies/${companyId}/users`);
            const snapshot = await getDocs(usersRef);

            return snapshot.docs
                .map(doc => {
                    const data = doc.data();
                    // Only return users who have a valid location
                    if (!data.lastLocation || !data.lastLocation.lat || !data.lastLocation.lng) {
                        return null;
                    }

                    return {
                        id: doc.id,
                        name: data.name || 'Unknown User',
                        driver: data.name || 'Unknown', // Map expects 'driver'
                        lat: data.lastLocation.lat,
                        lng: data.lastLocation.lng,
                        status: data.status || 'idle',
                        speed: data.speed || 0,
                        heading: data.heading || 0,
                        updatedAt: data.lastLocation.timestamp
                    };
                })
                .filter(u => u !== null);
        } catch (error: any) {
            // Handle permission denied gracefully
            if (error?.code === 'permission-denied') {
            } else {
                console.error('Error fetching active fleet:', error);
            }
            return [];
        }
    }

    /**
     * Create a new safety alert
     */
    async createAlert(alert: Omit<SafetyAlert, 'id' | 'companyId' | 'timestamp' | 'status'>): Promise<string> {
        const companyId = this.getCompanyIdFromContext();
        if (!companyId) {
            throw new Error('Tenant context required to create alerts');
        }

        const alertsRef = collection(db, `companies/${companyId}/${this.ALERTS_COLLECTION}`);

        const newAlert = {
            ...alert,
            companyId,
            timestamp: Timestamp.now(),
            status: 'new'
        };

        const docRef = await addDoc(alertsRef, newAlert);
        return docRef.id;
    }

    /**
     * Acknowledge an alert
     */
    async acknowledgeAlert(alertId: string, userId: string): Promise<void> {
        const companyId = this.getCompanyIdFromContext();
        if (!companyId) {
            throw new Error('Tenant context required');
        }

        const alertRef = doc(db, `companies/${companyId}/${this.ALERTS_COLLECTION}`, alertId);

        await updateDoc(alertRef, {
            status: 'acknowledged',
            acknowledgedBy: userId,
            acknowledgedAt: Timestamp.now()
        });
    }

    /**
     * Helper: Convert Firestore doc to SafetyAlert
     */
    private convertDocToAlert(doc: any): SafetyAlert {
        const data = doc.data();
        return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date(),
            acknowledgedAt: data.acknowledgedAt?.toDate(),
            resolvedAt: data.resolvedAt?.toDate(),
        } as SafetyAlert;
    }
}

export const safetyService = new SafetyService();
